import { useState, useMemo } from "react";
import { supabase } from "../supabase";
import { CATEGORIES as FALLBACK, SECTION_ICONS } from "../data";
import type { CategoryRow } from "../hooks/useCategories";

type BudgetType = 'הכנסה' | 'קבוע' | 'משתנה';

interface Props {
  current: string;
  catSearch: string;
  setCatSearch: (v: string) => void;
  onSelect: (cat: string) => void;
  /** סקציות גלובליות — אם לא מועבר, נפול חזרה לקבוע הקוד */
  categories?: Record<string, string[]>;
  /** שורות גולמיות מה-DB — לצורך חלוקה לקבוע/משתנה/הכנסה */
  rows?: CategoryRow[];
  /** קטגוריות אישיות של הלקח */
  clientCats?: string[];
  /** id הלקוח — דרוש כדי להציג כפתור "הוסף קטגוריה אישית" */
  clientId?: number | string | null;
  /** נקרא לאחר הוספת קטגוריה אישית, כדי לרענן */
  onCategoryAdded?: () => void;
  /** קטגוריות מוסתרות */
  hiddenCats?: string[];
  /** נקרא עם הרשימה המעודכנת אחרי שינוי הסתרה */
  onHiddenCatsChange?: (cats: string[]) => void;
  /** אם מועבר — מציג רק קטגוריות אלה (מהתסריט הפעיל) + קטגוריות אישיות */
  scenarioCats?: string[] | null;
}

export function CategoryPicker({
  current, catSearch, setCatSearch, onSelect,
  categories, rows, clientCats = [], clientId, onCategoryAdded,
  hiddenCats = [], onHiddenCatsChange, scenarioCats,
}: Props) {
  // אם יש תסריט פעיל — הצג רק קטגוריות מהתסריט (לפי sections)
  // אחרת — הצג הכל
  const baseCats = useMemo(() => {
    const allCats = categories || FALLBACK;
    if (!scenarioCats) return allCats;
    const scenSet = new Set(scenarioCats);
    const result: Record<string, string[]> = {};
    Object.entries(allCats).forEach(([section, names]) => {
      const filtered = names.filter(n => scenSet.has(n));
      if (filtered.length > 0) result[section] = filtered;
    });
    return result;
  }, [categories, scenarioCats]);

  const cats = baseCats;

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState("");
  const [selectedType, setSelectedType] = useState<BudgetType | null>(null);
  const [showOnlyStar, setShowOnlyStar] = useState(false);
  const [managing, setManaging] = useState(false);
  const [manageTab, setManageTab] = useState<"builtin"|"personal">("builtin");
  const [showHidden, setShowHidden] = useState(false);
  const [deleteCheck, setDeleteCheck] = useState<{
    cat: string;
    usages: {monthLabel:string;date:string;name:string;amount:number;submissionId:string;txIndex:number}[];
    subTransactions: Record<string, any[]>;
  } | null>(null);
  const [deleteChecking, setDeleteChecking] = useState(false);
  const [reclassOpenFor, setReclassOpenFor] = useState<string|null>(null);
  const [reclassSearch, setReclassSearch] = useState("");

  const hiddenSet = useMemo(() => new Set(hiddenCats), [hiddenCats]);

  // קטגוריות מסוננות לפי סוג נבחר + ללא המוסתרות (אלא אם במצב ניהול)
  const visibleCats = useMemo(() => {
    let base = cats;
    if (rows && selectedType) {
      const typeNames = new Set(rows.filter(r => r.budget_type === selectedType).map(r => r.name));
      const result: Record<string, string[]> = {};
      Object.entries(cats).forEach(([section, names]) => {
        const filtered = names.filter(n => typeNames.has(n));
        if (filtered.length > 0) result[section] = filtered;
      });
      base = result;
    }
    if (managing) return base; // במצב ניהול רואים הכל
    const result: Record<string, string[]> = {};
    Object.entries(base).forEach(([section, names]) => {
      const filtered = names.filter(n => !hiddenSet.has(n));
      if (filtered.length > 0) result[section] = filtered;
    });
    return result;
  }, [cats, rows, selectedType, hiddenSet, managing]);

  // מפה שם → budget_type לתצוגה בחיפוש
  const budgetTypeMap = useMemo(() => {
    const m: Record<string, string> = {};
    (rows || []).forEach(r => { m[r.name] = r.budget_type; });
    return m;
  }, [rows]);

  // קטגוריות אישיות מחולקות לפי סוג (לתצוגת ⭐)
  const personalCatsByType = useMemo(() => {
    const map: Record<string, string[]> = { 'הכנסה': [], 'קבוע': [], 'משתנה': [] };
    clientCats.filter(c => !hiddenSet.has(c)).forEach(c => {
      const t = budgetTypeMap[c] || 'משתנה';
      if (!map[t]) map[t] = [];
      map[t].push(c);
    });
    return map;
  }, [clientCats, budgetTypeMap, hiddenSet]);

  // חיפוש — תמיד על כל הקטגוריות (ללא תלות בסוג נבחר), ובלי המוסתרות
  const allCatsFlat = useMemo(() => {
    const global = Object.values(cats).flat().filter(n => !hiddenSet.has(n));
    const personal = clientCats.filter(n => !hiddenSet.has(n));
    return [...global, ...personal];
  }, [cats, clientCats, hiddenSet]);

  const filtered = catSearch.trim()
    ? allCatsFlat.filter(c => c.toLowerCase().includes(catSearch.trim().toLowerCase()))
    : null;

  const hideCategory = (cat: string) => {
    const next = [...hiddenCats, cat];
    onHiddenCatsChange?.(next);
  };

  const restoreCategory = (cat: string) => {
    const next = hiddenCats.filter(c => c !== cat);
    onHiddenCatsChange?.(next);
  };

  const handleDeletePersonalCat = async (cat: string) => {
    if (!clientId) return;
    setDeleteChecking(true);
    const { data: subs } = await supabase
      .from("portfolio_submissions")
      .select("id, label, transactions")
      .eq("client_id", Number(clientId));
    setDeleteChecking(false);
    const usages: {monthLabel:string;date:string;name:string;amount:number;submissionId:string;txIndex:number}[] = [];
    const subTransactions: Record<string, any[]> = {};
    (subs || []).forEach((sub: any) => {
      subTransactions[sub.id] = sub.transactions || [];
      (sub.transactions || []).forEach((tx: any, txIndex: number) => {
        if (tx.cat === cat) usages.push({ monthLabel: sub.label, date: tx.date, name: tx.name, amount: tx.amount, submissionId: sub.id, txIndex });
      });
    });
    setDeleteCheck({ cat, usages, subTransactions });
  };

  const reclassifyUsage = async (submissionId: string, txIndex: number, newCat: string) => {
    if (!deleteCheck) return;
    const txs = [...deleteCheck.subTransactions[submissionId]];
    txs[txIndex] = { ...txs[txIndex], cat: newCat };
    await supabase.from("portfolio_submissions").update({ transactions: txs }).eq("id", submissionId);
    const newSubTxs = { ...deleteCheck.subTransactions, [submissionId]: txs };
    const newUsages = deleteCheck.usages.filter(u => !(u.submissionId === submissionId && u.txIndex === txIndex));
    setDeleteCheck({ ...deleteCheck, usages: newUsages, subTransactions: newSubTxs });
  };

  const confirmDeletePersonalCat = async (cat: string) => {
    await supabase.from("categories").delete().eq("client_id", Number(clientId)).eq("name", cat);
    setDeleteCheck(null);
    onCategoryAdded?.();
  };

  const allCategoryOptions = useMemo(() => {
    const global = Object.values(cats).flat();
    const personal = clientCats.filter(c => c !== deleteCheck?.cat);
    return [...new Set([...global, ...personal])].sort();
  }, [cats, clientCats, deleteCheck?.cat]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name || !clientId) return;
    setSaving(true);
    setAddError("");
    // חשב sort_order גבוה מכל הקטגוריות האישיות הקיימות
    const { data: maxOrdRow } = await supabase
      .from("categories").select("sort_order")
      .eq("client_id", Number(clientId))
      .order("sort_order", { ascending: false }).limit(1).maybeSingle();
    const nextOrder = ((maxOrdRow as any)?.sort_order || 0) + 10;

    const { error } = await supabase.from("categories").insert([{
      name,
      section: "הקטגוריות שלי",
      budget_type: selectedType || 'משתנה',
      client_id: Number(clientId),
      is_active: true,
      is_ignored: false,
      sort_order: nextOrder,
    }]);
    setSaving(false);
    if (error) {
      setAddError(error.message.includes("unique") ? "קטגוריה בשם זה כבר קיימת — חפש אותה ברשימת הקטגוריות" : "שגיאה בשמירה");
      return;
    }
    // רשום ב-change_log — כישלון לא ימנע הוספת הקטגוריה
    supabase.from("client_change_log").insert([{
      client_id: Number(clientId),
      event_type: "category_created",
      details: { category_name: name, budget_type: selectedType || 'משתנה' },
    }]).then(({ error }) => { if (error) console.error("change_log insert error:", error); });
    setAdding(false);
    setNewName("");
    onCategoryAdded?.();
    onSelect(name);
  };

  const btnStyle = (cat: string, isClient = false, isHiding = false) => ({
    padding: "3px 10px",
    borderRadius: 14,
    fontSize: 13,
    cursor: "pointer" as const,
    fontFamily: "inherit",
    border: `1px solid ${current === cat ? (isClient ? "var(--gold)" : "var(--green-mid)") : isHiding ? "var(--red)" : "var(--border)"}`,
    background: current === cat ? (isClient ? "var(--gold-light)" : "var(--green-pale)") : isHiding ? "var(--red-light)" : "var(--surface2)",
    color: current === cat ? (isClient ? "var(--gold)" : "var(--green-mid)") : isHiding ? "var(--red)" : "var(--text)",
    fontWeight: current === cat ? 700 : 400,
    opacity: isHiding ? 0.7 : 1,
  });

  const typeLabels: { type: BudgetType; label: string; color: string }[] = [
    { type: 'הכנסה',  label: 'הכנסות',        color: 'var(--green-mid)' },
    { type: 'קבוע',   label: 'הוצאות קבועות', color: 'var(--gold)' },
    { type: 'משתנה',  label: 'הוצאות משתנות', color: 'var(--red)' },
  ];

  const canManage = !!onHiddenCatsChange;

  const checkSvg = <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg>;

  return (
    <div style={{ marginTop: 8, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding:"12px 14px", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }} onClick={e => e.stopPropagation()}>
      {/* ── מודל מחיקת קטגוריה אישית ── */}
      {deleteCheck && (
        <>
          <div onClick={() => setDeleteCheck(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:"var(--z-back)" }} />
          <div style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:16, padding:24, zIndex:"var(--z-modal)", width:360, maxHeight:"70vh", overflow:"auto", direction:"rtl" }}>
            {deleteCheck.usages.length === 0 ? (
              <>
                <div style={{ fontWeight:700, fontSize:17, marginBottom:10, color:"var(--red)" }}>מחיקת קטגוריה</div>
                <div style={{ fontSize:15, color:"var(--text-dim)", marginBottom:20, lineHeight:1.6 }}>
                  למחוק לצמיתות את הקטגוריה <strong>"{deleteCheck.cat}"</strong>?<br/>
                  הקטגוריה אינה בשימוש בשום תנועה.
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={() => confirmDeletePersonalCat(deleteCheck.cat)} style={{ flex:1, padding:"9px 0", borderRadius:8, border:"none", background:"var(--red)", color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>מחק</button>
                  <button onClick={() => setDeleteCheck(null)} style={{ padding:"9px 16px", borderRadius:8, border:"1px solid var(--border)", background:"transparent", color:"var(--text-dim)", fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>ביטול</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight:700, fontSize:17, marginBottom:6, color:"var(--red)" }}>הקטגוריה בשימוש</div>
                <div style={{ fontSize:14, color:"var(--text-dim)", marginBottom:12 }}>
                  הקטגוריה <strong>"{deleteCheck.cat}"</strong> בשימוש ב-{deleteCheck.usages.length} תנועות.<br/>
                  שנה את הסיווג לכל תנועה כדי לאפשר מחיקה.
                </div>
                <div style={{ maxHeight:300, overflowY:"auto", border:"1px solid var(--border)", borderRadius:8 }}>
                  {(() => {
                    const byMonth: Record<string, typeof deleteCheck.usages> = {};
                    deleteCheck.usages.forEach(u => { (byMonth[u.monthLabel] = byMonth[u.monthLabel] || []).push(u); });
                    return Object.entries(byMonth).map(([month, txs]) => (
                      <div key={month}>
                        <div style={{ padding:"6px 12px", background:"var(--green-pale)", fontWeight:700, fontSize:13, color:"var(--green-deep)", position:"sticky", top:0 }}>{month}</div>
                        {txs.map((u, i) => {
                            const uid = `${u.submissionId}-${u.txIndex}`;
                            const isOpen = reclassOpenFor === uid;
                            const opts = allCategoryOptions.filter(c => c !== deleteCheck.cat && (!reclassSearch.trim() || c.toLowerCase().includes(reclassSearch.trim().toLowerCase())));
                            return (
                              <div key={i} style={{ padding:"8px 12px", borderBottom:"1px solid var(--border)" }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                                  <div>
                                    <div style={{ fontWeight:600, fontSize:13 }}>{u.name}</div>
                                    <div style={{ color:"var(--text-dim)", fontSize:12 }}>{u.date}</div>
                                  </div>
                                  <span style={{ fontWeight:700, color:"var(--red)", fontSize:13 }}>₪{Number(u.amount).toLocaleString()}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => { setReclassOpenFor(isOpen ? null : uid); setReclassSearch(""); }}
                                  style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 10px", borderRadius:8, border:`1px solid ${isOpen ? "var(--green-mid)" : "var(--border)"}`, background: isOpen ? "var(--green-pale)" : "var(--surface2)", color:"var(--text)", fontSize:13, fontFamily:"inherit", cursor:"pointer" }}
                                >
                                  <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>שנה סיווג מ<strong> {deleteCheck.cat}</strong></span>
                                  <span style={{ color:"var(--text-dim)" }}>{isOpen ? "▲" : "▼"}</span>
                                </button>
                                {isOpen && (
                                  <div style={{ marginTop:6, border:"1px solid var(--border)", borderRadius:8, overflow:"hidden" }}>
                                    <input
                                      autoFocus
                                      value={reclassSearch}
                                      onChange={e => setReclassSearch(e.target.value)}
                                      placeholder="חפש קטגוריה..."
                                      style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px", border:"none", borderBottom:"1px solid var(--border)", background:"var(--surface)", color:"var(--text)", fontSize:13, fontFamily:"inherit", outline:"none" }}
                                    />
                                    <div style={{ maxHeight:140, overflowY:"auto", padding:"6px 8px", display:"flex", flexWrap:"wrap", gap:5 }}>
                                      {opts.length === 0 && <span style={{ fontSize:13, color:"var(--text-dim)" }}>לא נמצאו תוצאות</span>}
                                      {opts.map(c => (
                                        <button key={c} type="button"
                                          onClick={() => { reclassifyUsage(u.submissionId, u.txIndex, c); setReclassOpenFor(null); setReclassSearch(""); }}
                                          style={{ padding:"4px 11px", borderRadius:14, fontSize:13, cursor:"pointer", fontFamily:"inherit", border:`1px solid ${clientCats.includes(c) ? "var(--gold)" : "var(--border)"}`, background: clientCats.includes(c) ? "var(--gold-light)" : "var(--surface2)", color: clientCats.includes(c) ? "var(--gold)" : "var(--text)", fontWeight:400 }}
                                        >
                                          {c}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ));
                  })()}
                </div>
                <button onClick={() => setDeleteCheck(null)} style={{ marginTop:14, width:"100%", padding:"9px 0", borderRadius:8, border:"none", background:"var(--green-mid)", color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>סגור</button>
              </>
            )}
          </div>
        </>
      )}

      {/* ── כותרת: נהל קטגוריות (ימין) + הוסף קטגוריה (שמאל) ── */}
      {canManage && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <button
            type="button"
            onClick={() => { setManaging(m => !m); setShowHidden(false); setManageTab("builtin"); }}
            style={{
              background: managing ? "var(--red-light)" : "transparent",
              border: `1px solid ${managing ? "var(--red)" : "var(--border)"}`,
              borderRadius: 8, padding: "3px 10px", fontSize: 12,
              color: managing ? "var(--red)" : "var(--text-dim)",
              cursor: "pointer", fontFamily: "inherit", fontWeight: managing ? 700 : 400,
              display: "inline-flex", alignItems: "center", gap: 5,
            }}
          >
            {managing
            ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg> סיום ניהול</>
            : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> נהל קטגוריות</>
          }
          </button>
          {clientId && !managing && !adding && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setAdding(true); setAddError(""); setSelectedType(null); setNewName(""); }}
              style={{ background:"var(--green-mint)", border:"1px solid var(--green-soft)", borderRadius:8, color:"var(--green-deep)", fontSize:12, cursor:"pointer", fontFamily:"inherit", padding:"3px 10px", display:"inline-flex", alignItems:"center", gap:4, fontWeight:600 }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              הוסף קטגוריה אישית
            </button>
          )}
        </div>
      )}

      {/* ── בחירת סוג ── */}
      {rows && !adding && (!managing || manageTab === "builtin") && (
        <>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 10, fontWeight: 600, textAlign: "center" }}>בחר סוג קטגוריה</div>
          <div style={{ display: "flex", gap: 0, marginBottom: 10, borderBottom: "1px solid var(--border)" }}>
            {typeLabels.map(({ type, label, color }) => (
              <button
                type="button"
                key={type}
                onClick={() => { setSelectedType(prev => prev === type ? null : type); setShowOnlyStar(false); }}
                style={{
                  flex: 1, padding: "5px 6px", fontSize: 12, fontFamily: "inherit",
                  border: "none",
                  borderBottom: `2px solid ${selectedType === type ? color : "transparent"}`,
                  marginBottom: -1,
                  background: "transparent",
                  color: selectedType === type ? color : "var(--text-dim)",
                  fontWeight: selectedType === type ? 700 : 400,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
            {clientCats.length > 0 && (
              <button
                type="button"
                onClick={() => { setShowOnlyStar(s => !s); setSelectedType(null); }}
                style={{
                  padding: "5px 10px", fontSize: 12, fontFamily: "inherit",
                  border: "none",
                  borderBottom: `2px solid ${showOnlyStar ? "var(--gold)" : "transparent"}`,
                  marginBottom: -1,
                  background: "transparent",
                  color: showOnlyStar ? "var(--gold)" : "var(--text-dim)",
                  fontWeight: showOnlyStar ? 700 : 400,
                  cursor: "pointer", flexShrink: 0, display:"inline-flex", alignItems:"center", gap:4,
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{ flexShrink:0 }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> שלי
              </button>
            )}
          </div>
        </>
      )}

      {/* ── חיפוש — מוסתר בעת הוספת קטגוריה ── */}
      {!adding && (
        <input
          value={catSearch}
          onChange={e => setCatSearch(e.target.value)}
          placeholder="חפש סעיף..."
          style={{
            width: "100%", background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: 8, padding: "6px 12px", color: "var(--text)", fontSize: 14,
            fontFamily: "inherit", outline: "none", marginBottom: 8, boxSizing: "border-box",
          }}
        />
      )}

      {/* ── הוסף קטגוריה אישית — טופס בלבד כשפתוח ── */}
      {clientId && !managing && adding && (
        <div style={{ marginBottom: 8 }}>
          {true && (
            <div style={{ background: "var(--gold-light)", border: "1px solid var(--gold)", borderRadius: 10, padding: "12px 12px 10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--gold)", display:"inline-flex", alignItems:"center", gap:5 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{ flexShrink:0 }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>קטגוריה אישית חדשה</span>
                <button type="button" onClick={() => { setAdding(false); setNewName(""); setAddError(""); setSelectedType(null); }}
                  style={{ background: "none", border: "none", color: "var(--text-dim)", fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
              </div>

              {/* שלב 1 — בחר סוג */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 6, fontWeight: 600 }}>1. בחר סוג:</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {([
                    { type: 'הכנסה' as BudgetType,  label: 'הכנסה',         color: 'var(--green-mid)' },
                    { type: 'קבוע'  as BudgetType,  label: 'הוצאה קבועה',  color: 'var(--gold)' },
                    { type: 'משתנה' as BudgetType,  label: 'הוצאה משתנה',  color: 'var(--red)' },
                  ]).map(({ type, label, color }) => (
                    <button type="button" key={type} onClick={() => setSelectedType(type)}
                      style={{ flex: 1, padding: "6px 4px", fontSize: 13, fontFamily: "inherit", cursor: "pointer",
                        border: `1.5px solid ${selectedType === type ? color : "var(--border)"}`,
                        borderRadius: 8,
                        background: selectedType === type ? `color-mix(in srgb, ${color} 15%, transparent)` : "var(--surface2)",
                        color: selectedType === type ? color : "var(--text-dim)",
                        fontWeight: selectedType === type ? 700 : 400 }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* שלב 2 — שם (רק אחרי בחירת סוג) */}
              {selectedType && (
                <div>
                  <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 6, fontWeight: 600 }}>2. שם הקטגוריה:</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input
                      autoFocus
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      placeholder="למשל: טיפולי שיניים"
                      maxLength={50}
                      style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--border)",
                        background: "var(--surface)", color: "var(--text)", fontSize: 15, fontFamily: "inherit", outline: "none" }}
                      onKeyDown={e => {
                        if (e.key === "Enter") { e.preventDefault(); handleAdd(); }
                        if (e.key === "Escape") { setAdding(false); setNewName(""); }
                      }}
                    />
                    <button type="button" onClick={handleAdd} disabled={!newName.trim() || saving}
                      style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--green-mid)", color: "#fff",
                        fontSize: 15, cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
                        opacity: !newName.trim() ? 0.5 : 1 }}>
                      {saving ? "..." : "שמור"}
                    </button>
                  </div>
                  {addError && <div style={{ fontSize: 13, color: "var(--red)", marginTop: 4 }}>{addError}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── טאבים במצב ניהול ── */}
      {managing && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            {(["builtin", "personal"] as const).map(tab => {
              const label = tab === "builtin" ? "מובנות" : "אישיות";
              const active = manageTab === tab;
              return (
                <button key={tab} type="button" onClick={() => setManageTab(tab)}
                  style={{
                    flex: 1, padding: "7px 0", borderRadius: 8, fontSize: 14, fontFamily: "inherit",
                    cursor: "pointer", fontWeight: active ? 700 : 400,
                    border: `1.5px solid ${active ? "var(--green-mid)" : "var(--border)"}`,
                    background: active ? "var(--green-pale)" : "var(--surface2)",
                    color: active ? "var(--green-mid)" : "var(--text-dim)",
                  }}>
                  {label}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 13, color: manageTab === "builtin" ? "var(--red)" : "var(--gold)", marginBottom: 2, padding: "6px 10px", background: manageTab === "builtin" ? "var(--red-light)" : "var(--gold-light)", borderRadius: 8 }}>
            {manageTab === "builtin" ? "לחץ על קטגוריה להסתרה / הצגה שלה" : "לחץ על קטגוריה למחיקה שלה"}
          </div>
        </div>
      )}

      {/* ── חיפוש חופשי ── */}
      {filtered ? (
        <div style={{ position:"relative" }}>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {filtered.length === 0 && (
              <span style={{ fontSize: 13, color: "var(--text-dim)" }}>לא נמצאו תוצאות</span>
            )}
            {filtered.length > 0 && (() => {
              const clientSet = new Set(clientCats);
              const groups: { label: string; color: string; cats: string[] }[] = [
                { label: 'הכנסות',        color: 'var(--green-mid)', cats: filtered.filter(c => budgetTypeMap[c] === 'הכנסה') },
                { label: 'הוצאות קבועות', color: 'var(--gold)',      cats: filtered.filter(c => budgetTypeMap[c] === 'קבוע') },
                { label: 'הוצאות משתנות', color: 'var(--red)',       cats: filtered.filter(c => budgetTypeMap[c] === 'משתנה') },
              ];
              const untyped = filtered.filter(c => clientSet.has(c) && !budgetTypeMap[c]);
              untyped.forEach(c => { if (!groups[2].cats.includes(c)) groups[2].cats.push(c); });
              return groups.filter(g => g.cats.length > 0).map(g => (
                <div key={g.label} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: g.color, fontWeight: 700, marginBottom: 5, paddingBottom: 3, borderBottom: `1px solid var(--border)` }}>
                    {g.label}
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {g.cats.map(cat => {
                      const isStar = clientSet.has(cat);
                      return (
                        <button type="button" key={cat}
                          onClick={e => { e.stopPropagation(); managing ? hideCategory(cat) : onSelect(cat); }}
                          style={{ ...btnStyle(cat, isStar, managing), display:"inline-flex", alignItems:"center", gap:4 }}>
                          {!managing && current === cat && checkSvg}
                          {managing ? `× ${cat}` : cat}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:28, background:"linear-gradient(to bottom, transparent, var(--surface))", pointerEvents:"none" }} />
        </div>
      ) : showOnlyStar ? (
        /* ── תצוגת שלי בלבד ── */
        <div style={{ position:"relative" }}>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {clientCats.length === 0 && (
              <span style={{ fontSize: 13, color: "var(--text-dim)" }}>אין קטגוריות אישיות עדיין</span>
            )}
            {(['הכנסה', 'קבוע', 'משתנה'] as BudgetType[]).map(type => {
              const typeCats = personalCatsByType[type] || [];
              if (typeCats.length === 0) return null;
              const tl = typeLabels.find(t => t.type === type)!;
              return (
                <div key={type} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: tl.color, fontWeight: 700, marginBottom: 5, paddingBottom: 3, borderBottom: "1px solid var(--border)" }}>
                    {tl.label}
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {typeCats.map(cat => (
                      <button type="button" key={cat}
                        onClick={e => { e.stopPropagation(); onSelect(cat); }}
                        style={{ ...btnStyle(cat, true, false), display:"inline-flex", alignItems:"center", gap:4 }}>
                        {current === cat && checkSvg}
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:28, background:"linear-gradient(to bottom, transparent, var(--surface))", pointerEvents:"none" }} />
        </div>
      ) : (
        /* ── תצוגה לפי סקציות ── */
        <div style={{ position:"relative" }}>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {(!managing || manageTab === "builtin") && Object.entries(visibleCats).map(([group, groupCats]) => (
              <div key={group} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--text-dim)", fontWeight: 700, marginBottom: 5, paddingBottom: 3, borderBottom: "1px solid var(--border)" }}>
                  {group}
                </div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {groupCats.map(cat => {
                    const isHidden = hiddenSet.has(cat);
                    if (managing) {
                      return (
                        <button type="button" key={cat}
                          onClick={e => { e.stopPropagation(); isHidden ? restoreCategory(cat) : hideCategory(cat); }}
                          title={isHidden ? "לחץ להחזיר לתצוגה" : "לחץ להסתיר"}
                          style={{
                            padding: "3px 10px", borderRadius: 14, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                            border: `1px solid ${isHidden ? "var(--border)" : "var(--green-soft)"}`,
                            background: isHidden ? "var(--surface2)" : "var(--green-mint)",
                            color: isHidden ? "var(--text-dim)" : "var(--green-deep)",
                            textDecoration: isHidden ? "line-through" : "none",
                            opacity: isHidden ? 0.6 : 1,
                          }}>
                          {isHidden ? `× ${cat}` : cat}
                        </button>
                      );
                    }
                    return (
                      <button type="button" key={cat}
                        onClick={e => { e.stopPropagation(); onSelect(cat); }}
                        style={{ ...btnStyle(cat, false, false), display:"inline-flex", alignItems:"center", gap:4 }}>
                        {current === cat && checkSvg}
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {selectedType && !managing && (personalCatsByType[selectedType]?.length ?? 0) > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, marginBottom: 5, paddingBottom: 3, borderBottom: "1px solid var(--border)" }}>
                  הקטגוריות שלי
                </div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {(personalCatsByType[selectedType] || []).map(cat => (
                    <button type="button" key={cat}
                      onClick={e => { e.stopPropagation(); onSelect(cat); }}
                      style={{ ...btnStyle(cat, true, false), display:"inline-flex", alignItems:"center", gap:4 }}>
                      {current === cat && checkSvg}
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {clientCats.length > 0 && (!managing || manageTab === "personal") && !selectedType && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, marginBottom: 5, paddingBottom: 3, borderBottom: "1px solid var(--border)" }}>
                  הקטגוריות שלי
                </div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {clientCats.map(cat => {
                    if (managing) {
                      const isHidden = hiddenSet.has(cat);
                      return (
                        <button type="button" key={cat}
                          onClick={e => { e.stopPropagation(); isHidden ? restoreCategory(cat) : hideCategory(cat); }}
                          title={isHidden ? "לחץ להחזיר לתצוגה" : "לחץ להסתיר"}
                          style={{
                            padding: "3px 10px", borderRadius: 14, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                            border: `1px solid ${isHidden ? "var(--border)" : "var(--gold)"}`,
                            background: isHidden ? "var(--surface2)" : "rgba(247,194,56,0.12)",
                            color: isHidden ? "var(--text-dim)" : "var(--gold)",
                            textDecoration: isHidden ? "line-through" : "none",
                            opacity: isHidden ? 0.6 : 1,
                          }}>
                          {isHidden ? `× ${cat}` : cat}
                        </button>
                      );
                    }
                    if (hiddenSet.has(cat)) return null;
                    return (
                      <button type="button" key={cat}
                        onClick={e => { e.stopPropagation(); onSelect(cat); }}
                        style={{ ...btnStyle(cat, true, false), display:"inline-flex", alignItems:"center", gap:4 }}>
                        {current === cat && checkSvg}
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:28, background:"linear-gradient(to bottom, transparent, var(--surface))", pointerEvents:"none" }} />
        </div>
      )}

      {/* ── הוסף קטגוריה אישית — כפתור inline קטן בתחתית ── */}

      {/* ── קטגוריות מוסתרות (רק כשלא במצב ניהול) ── */}
      {canManage && !managing && hiddenCats.length > 0 && (
        <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 8 }}>
          <button
            type="button"
            onClick={() => setShowHidden(h => !h)}
            style={{ background: "none", border: "none", fontSize: 13, color: "var(--text-dim)", cursor: "pointer", fontFamily: "inherit", padding: 0, marginBottom: 6 }}
          >
            {showHidden ? "▲" : "▼"} קטגוריות מוסתרות ({hiddenCats.length})
          </button>
          {showHidden && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {hiddenCats.map(cat => (
                <button type="button" key={cat} onClick={() => restoreCategory(cat)}
                  style={{ padding: "4px 11px", borderRadius: 14, fontSize: 14, cursor: "pointer", fontFamily: "inherit", border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text-dim)", opacity: 0.7 }}
                  title="לחץ להחזיר">↩ {cat}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
